import type { MeshData } from "@cfg/kernel";
import { useEffect, useMemo } from "react";
import * as THREE from "three";

/** Uploads a MeshData into a BufferGeometry, reusing buffers when sizes match. */
export function useMeshGeometry(mesh: MeshData | null): THREE.BufferGeometry | null {
  const geometry = useMemo(() => (mesh ? new THREE.BufferGeometry() : null), [mesh === null]);
  useEffect(() => {
    if (!geometry || !mesh) return;
    geometry.setAttribute("position", new THREE.BufferAttribute(mesh.positions, 3));
    if (mesh.normals) geometry.setAttribute("normal", new THREE.BufferAttribute(mesh.normals, 3));
    else {
      geometry.deleteAttribute("normal");
    }
    geometry.setIndex(new THREE.BufferAttribute(mesh.indices, 1));
    if (!mesh.normals) geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    geometry.attributes.position.needsUpdate = true;
  }, [geometry, mesh]);
  useEffect(() => () => geometry?.dispose(), [geometry]);
  return geometry;
}

export interface ModelDisplay {
  /** Draw triangle edges instead of the shaded surface. */
  wireframe?: boolean;
  /** Overlay feature edges (dihedral angle above `edgeThreshold`) on the model. */
  edges?: boolean;
  edgeColor?: string;
  /** Degrees; flatter creases are not drawn. */
  edgeThreshold?: number;
}

/** Feature-edge geometry built straight from the mesh, so it follows every rebuild. */
function useEdgesGeometry(mesh: MeshData | null, enabled: boolean, threshold: number): THREE.EdgesGeometry | null {
  const edges = useMemo(() => {
    if (!enabled || !mesh) return null;
    const src = new THREE.BufferGeometry();
    src.setAttribute("position", new THREE.BufferAttribute(mesh.positions, 3));
    src.setIndex(new THREE.BufferAttribute(mesh.indices, 1));
    const out = new THREE.EdgesGeometry(src, threshold);
    src.dispose();
    return out;
  }, [mesh, enabled, threshold]);
  useEffect(() => () => edges?.dispose(), [edges]);
  return edges;
}

export function Model({ mesh, material, display = {} }: { mesh: MeshData | null; material: THREE.Material; display?: ModelDisplay }) {
  const { wireframe = false, edges = false, edgeColor = "#1b1f27", edgeThreshold = 20 } = display;
  const geometry = useMeshGeometry(mesh);
  const edgeGeometry = useEdgesGeometry(mesh, edges, edgeThreshold);
  if (!geometry) return null;
  return (
    <>
      {wireframe ? (
        <mesh geometry={geometry}>
          <meshBasicMaterial color={edgeColor} wireframe transparent opacity={0.55} />
        </mesh>
      ) : (
        <mesh geometry={geometry} material={material} castShadow receiveShadow />
      )}
      {edgeGeometry && (
        <lineSegments geometry={edgeGeometry} renderOrder={1}>
          <lineBasicMaterial color={edgeColor} />
        </lineSegments>
      )}
    </>
  );
}
